import { Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useRef } from "react";
import { useParams } from "react-router";
import { getNumberOfDataSubjects, useProcessingActivityOverviewDataMapping } from "./ProcessingActivityOverview.hooks";
import { useProcessingActivityOverviewStyles } from "./ProcessingActivityOverview.styles";
import { DotLegalButton, DotLegalCompletedPercentage, DotLegalPageHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation.ts";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { reactPlugin } from "../../AppInsights";
import { useAppInsightsContext, useTrackMetric, withAITracking } from "@microsoft/applicationinsights-react-js";
import { TrackingEvent, useTrackAIEvent } from "../hooks/useTracking";
import { SecurityMeasureOverviewModel } from "../dataSubjectStep/DataSubjectStep.types";
import CheckIcon from "@mui/icons-material/Check";
import RiskIndicator from "../../riskAssessment/riskIndicator/RiskIndicator";
import { RiskAssessmentType } from "../../riskAssessment/RiskAssessment.types";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import ProcessingActivityDeleteDialog from "../processingActivityDeleteDialog/ProcessingActivityDeleteDialog";
import EntityRiskAreasDialog from "../../risk/entityRiskAreasDialog/EntityRiskAreasDialog";
import RiskAreaRisksTile from "../../risk/riskAreaRisksTile/RiskAreaRisksTile";
import { Trans } from "react-i18next";
import { useNavMenuDataMapping } from "../../main/NavMenu/NavMenu.hooks";
import { hasAccessToProcessingActivitySteps } from "../processingActivities/processingActivityStepAccess";
import PolicyOverviewDialog from "./policyOverviewDialog/PolicyOverviewDialog";
import ProcessingActivityStatusDialog from "./processingActivityStatusDialog/ProcessingActivityStatusDialog";
import CommentDialog from "../../comment/commentDialog/CommentDialog";
import AuditLogDialog from "../../auditlogDialog/AuditLogDialog";
import { EntityType } from "@common/Common.types.ts";
import EntityGraphDialog from "@common/components/entityGraph/EntityGraphDialog.tsx";
import { useEntityGraphMenuItem } from "@common/components/entityGraph/EntityGraphMenuItem.hooks.ts";
import { ProcessingActivityStatus } from "../processingActivities/ProcessingActivities.types";
import { ProcessingActivityStatusManagement } from "../../auth/userContextProvider/UserContextProvider.types";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUrlProvider } from "../../useUrlProvider";
import { RiskAssessmentArea, RiskComplianceArea } from "../../riskAssessment/riskAssessments/RiskAssessments.types";
import { shouldDisableComments } from "../ProcessingActivity.hooks";
import ProcessingActivitySharingsTab from "./processingActivitySharingsTab/ProcessingActivitySharingsTab";
import ProcessingActivityDataCategoriesTab from "./processingActivityDataCategoriesTab/ProcessingActivityDataCategoriesTab";
import ProcessingActivityAssetsTab from "./processingActivityAssetsTab/ProcessingActivityAssetsTab";
import ProcessingActivityDocumentTab from "./processingActivityDocumentTab/ProcessingActivityDocumentTab";
import { NumberOfProcessorsEnum } from "../ProcessingActivity.types";
import ProcessingActivityClassificationTab from "./processingActivityClassificationTab/ProcessingActivityClassificationTab";
import { getPlanTab } from "../../plan/planTab/PlanTab";
import PageHeaderEditEntityButton, {
    PageHeaderEditEntityButtonMenuItem,
} from "../../common/components/pageHeaderEditEntityButton/PageHeaderEditEntityButton.tsx";
import RelatedTasksTab from "../../taskManagement/relatedTasksTab/RelatedTasksTab.tsx";
import { useAddTaskMenuItem } from "@common/components/addTaskMenuItem/AddTaskMenuItemContext.hooks.ts";
import { useGetRelatedTaskData } from "../../taskManagement/relatedTasksTab/RelatedTasksTab.hooks.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import ProcessingActivityReport from "../../processingActivityReport/ProcessingActivityReport.tsx";
import EntityDetailTopBar from "@common/components/entityDetailTopBar/EntityDetailTopBar";
import TopBarRow from "@common/components/entityDetailTopBar/TopBarRow";
import TopBarTile, { useTopBarTileSize } from "@common/components/entityDetailTopBar/TopBarTile";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";
import TopBarChipsTile from "@common/components/entityDetailTopBar/TopBarChipsTile";
import ProcessingActivityStatusChip from "../processingActivities/ProcessingActivityStatusChip";
import PlanTabs, { PlanTabContent } from "../../plan/planTab/PlanTabs.tsx";
import DotLegalNoteDialog from "../../common/components/dotlegalNote/dotLegalNoteDialog/DotLegalNoteDialog.tsx";
import ProcessingActivityAuditTab from "./processingActivityAuditTab/ProcessingActivityAuditTab.tsx";

function ProcessingActivityOverview() {
    const styles = useProcessingActivityOverviewStyles();
    const { translateString, translateDate, translateDataSubjectsEnum, translateProcessorsEnum } = useTranslation();
    const appInsights = useAppInsightsContext();
    const trackComponent = useTrackMetric(appInsights, "ProcessingActivityOverview");
    const { id } = useParams<{ id: string }>();
    const { entityGraphMenuItem, isEntityGraphOpen, closeEntityGraph } = useEntityGraphMenuItem();
    const hasTracked = useRef(false);
    const trackEvent = useTrackAIEvent();
    const { permissions } = useUserContext();
    const { addonsFeatures, dataProtectionFeatures, vendorManagementFeatures, platformFeatures } = usePlanContext();
    const { getProcessingActivityRiskScenariosDialog, getRiskAssessmentQuestionsDialog, getProcessingActivityValidationUrl } = useUrlProvider();
    const addTaskMenuItem = useAddTaskMenuItem();
    const topBarTileSize = useTopBarTileSize();

    if (!hasTracked.current) {
        trackComponent();
        hasTracked.current = true;
    }

    const {
        topBar,
        isTopBarLoading,
        refetchTopBar,
        tabCounts,
        refetchTabCounts,
        showNoteDialog,
        setShowNoteDialog,
        hasEnterpriseRiskManagementAccess,
        riskAssessmentData,
        riskAssessmentLoading,
        showDeleteDialog,
        setShowDeleteDialog,
        showRiskAreasDialog,
        setShowRiskAreasDialog,
        onProcessingActivityDeleteSuccess,
        setShowPolicyDialog,
        showPolicyDialog,
        showStatusDialog,
        setShowStatusDialog,
        showAuditLogDialog,
        setShowAuditLogDialog,
        showProcessingActivityReportDialog,
        setShowProcessingActivityReportDialog,
        canSubmitProcessingActivity,
        selectedTab,
        setSelectedTab,
    } = useProcessingActivityOverviewDataMapping(id);
    const { getTaskTabHeader } = useGetRelatedTaskData(id!);
    const { refetchMenuNotifications } = useNavMenuDataMapping();

    const getBreadCrumbs = () => {
        return [
            {
                name: translateString("processingActivities"),
                link: "/privacy/processingactivities",
            },
            {
                name: topBar?.name ? `${topBar?.name} ${!addonsFeatures.research ? `(${topBar?.userFriendlyId})` : ""}` : "",
                tooltip: isNullOrWhitespace(topBar?.description) ? undefined : <MentionInputRender value={topBar?.description ?? ""} linkColor={"white"} />,
            },
        ];
    };

    const getValidation = () => {
        const validation = topBar?.validation;

        if (validation) {
            const user = validation.validatedBy ? validation.validatedBy : translateString("deletedUser");

            return (
                <Box sx={styles.validationContainer}>
                    <Trans i18nKey="lastValidated" values={{ date: translateDate(validation.validatedDate), user: user }} />
                </Box>
            );
        }
        return undefined;
    };

    const OnProcessingActivityClick = (processingActivityName: string, processingActivityId: string) => {
        trackEvent(TrackingEvent.EditProcessingActivityClicked, {
            name: processingActivityName,
            id: processingActivityId,
        });
    };

    const getSecurityMeasures = (securityMeasures: Array<SecurityMeasureOverviewModel> | undefined) => {
        return securityMeasures?.map((s, i) => (
            <Box key={i} sx={styles.securityMeasure}>
                {s.isMet && <CheckIcon />}
                {s.name}
            </Box>
        ));
    };

    const getOwnerInfoBlock = () => {
        return (
            <Box sx={styles.ownerBlock}>
                {topBar?.responsible ?? "-"}
                {topBar?.responsibleActive === false && (
                    <DotLegalTooltip text={translateString("userDeactivated")}>
                        <ReportProblemOutlinedIcon fill="error" fontSize={"small"} />
                    </DotLegalTooltip>
                )}
            </Box>
        );
    };

    const ownerGrid = <TopBarTextTile label={translateString("responsible")} value={getOwnerInfoBlock()} isLoading={isTopBarLoading} />;

    const policyGrid = () => {
        const policyCount = topBar?.policies.length ?? 0;

        return (
            <TopBarNumberTile
                label={translateString("numberOfPolicies")}
                value={policyCount}
                onClick={policyCount > 0 ? () => setShowPolicyDialog(true) : undefined}
                isLoading={isTopBarLoading}
            />
        );
    };

    const showProcessingActivityStatusMenuItem = () => {
        let statusManagement = permissions.processingActivityStatusManagement;
        let processingActivityStatus = topBar?.processingActivityStatus!;

        if (statusManagement === ProcessingActivityStatusManagement.Full) {
            return true;
        } else if (statusManagement === ProcessingActivityStatusManagement.Draft) {
            return (
                processingActivityStatus === ProcessingActivityStatus.Draft ||
                processingActivityStatus === ProcessingActivityStatus.DraftSubmitted ||
                processingActivityStatus === ProcessingActivityStatus.AwaitingResearcher
            );
        }

        return statusManagement === ProcessingActivityStatusManagement.Regular;
    };

    const getTabLabel = (translationKey: string, count: number | undefined) => {
        return `${translateString(translationKey)}${count ? ` (${count})` : ""}`;
    };

    const getTabContent = () => {
        const content: Array<PlanTabContent> = [
            {
                label: getTabLabel("sharings", tabCounts.sharings),
                content: <ProcessingActivitySharingsTab processingActivityId={id!} />,
                index: 0,
            },
            {
                label: getTabLabel("dataCategories", tabCounts.dataCategories),
                content: <ProcessingActivityDataCategoriesTab processingActivityId={id!} />,
                index: 1,
            },
            {
                label: getTabLabel("assets", tabCounts.assets),
                content: <ProcessingActivityAssetsTab processingActivityId={id!} />,
                index: 2,
            },
        ];

        if (permissions.canAccessProcessingActivityDocuments) {
            content.push(
                getPlanTab(
                    getTabLabel("documents", dataProtectionFeatures.processingActivitiesDocuments ? tabCounts.documents : undefined),
                    <ProcessingActivityDocumentTab processingActivityId={id!} processingActivityName={topBar?.name} refetch={() => refetchTabCounts()} />,
                    3,
                    dataProtectionFeatures.processingActivitiesDocuments,
                    translateString("upgradeDataProtectionPlan")
                )
            );
        }

        if (vendorManagementFeatures.hasVendorManagement && (permissions.canManageInternalAssessment || permissions.canManageAuditGroups)) {
            content.push(
                getPlanTab(
                    getTabLabel("audits", tabCounts.audits),
                    <ProcessingActivityAuditTab processingActivityId={id!} processingActivityName={topBar?.name ?? ""} onAuditsChanged={refetchTabCounts} />,
                    4,
                    vendorManagementFeatures.processingActivityAssociateInternalAssessments,
                    translateString("upgradeVendorManagementPlan")
                )
            );
        }

        if (vendorManagementFeatures.hasVendorManagement && (permissions.canManageGroupEntities || permissions.companyPermissions)) {
            content.push(
                getPlanTab(
                    getTabLabel("classifications", tabCounts.classifications),
                    <ProcessingActivityClassificationTab processingActivityId={id!} onClassificationsChanged={refetchTabCounts} />,
                    5,
                    vendorManagementFeatures.systemClassifications,
                    translateString("upgradeVendorManagementPlan")
                )
            );
        }

        if (permissions.taskPermissions.read && !addonsFeatures.research) {
            content.push(
                getPlanTab(
                    getTaskTabHeader(),
                    <RelatedTasksTab forEntityId={id!} forEntityType={EntityType.ProcessingActivity} forEntityName={topBar?.name ?? ""} />,
                    6,
                    platformFeatures.canAccessRelationsBetweenEntities,
                    translateString("upgradePrivacyPlanToday")
                )
            );
        }

        return content;
    };

    const getNumberOfProcessors = () => {
        if (topBar) {
            if (topBar.numberOfProcessors) {
                if (topBar.numberOfProcessors === NumberOfProcessorsEnum.UserEnteredNumberOfProcessors) {
                    return topBar.noOfProcessors ?? undefined;
                }

                return translateProcessorsEnum(topBar.numberOfProcessors.toString());
            }
        }

        return 0;
    };

    const canSeePolicyGrid =
        dataProtectionFeatures.linkPoliciesAndProcessingActivities &&
        permissions.policyPermissions &&
        permissions.processingActivityPolicyPermissions?.permissions.read;

    const getEditMenuItems = () => {
        let menuItems: Array<PageHeaderEditEntityButtonMenuItem> = [];

        if (hasAccessToProcessingActivitySteps(permissions)) {
            menuItems.push({
                menuItem: translateString("editProcessingActivity"),
                onClick: () => {
                    OnProcessingActivityClick(topBar?.name! ?? "", topBar?.id! ?? "");
                },
                href: "edit",
            });
        }

        if (hasAccessToProcessingActivitySteps(permissions) && topBar?.canValidateProcessingActivity && !addonsFeatures.research) {
            menuItems.push({
                menuItem: translateString("validateProcessingActivity"),
                onClick: () => {},
                href: getProcessingActivityValidationUrl(id!),
                planMenuItemInfo: {
                    hasAccess: dataProtectionFeatures.processingActivityValidationFlow,
                    menuName: translateString("validateProcessingActivity"),
                    tooltip: translateString("upgradeDataProtectionPlan"),
                },
            });
        }

        if (permissions.canManageProcessingActivityNote) {
            menuItems.push({
                menuItem: translateString("note"),
                onClick: () => {
                    setShowNoteDialog(true);
                },
            });
        }

        if (showProcessingActivityStatusMenuItem() && topBar) {
            menuItems.push({
                menuItem: translateString("editProcessingActivityStatus"),
                onClick: () => {
                    setShowStatusDialog(true);
                },
                planMenuItemInfo: {
                    tooltip: translateString("upgradeDataProtectionPlan"),
                    menuName: translateString("editProcessingActivityStatus"),
                    hasAccess: dataProtectionFeatures.processingActivityStatus,
                },
            });
        }

        if (permissions.canAccessAuditLog) {
            menuItems.push({
                menuItem: translateString("auditlog"),
                onClick: () => {
                    setShowAuditLogDialog(true);
                },
                planMenuItemInfo: {
                    hasAccess: platformFeatures.auditLog,
                    menuName: translateString("auditlog"),
                    tooltip: translateString("upgradePrivacyPlanToday"),
                },
            });
        }

        if (addonsFeatures.research && permissions.reportTypes.length > 0 && permissions.processingActivityPermissions.read) {
            menuItems.push({
                menuItem: translateString("processingActivityReportDialog"),
                onClick: () => {
                    setShowProcessingActivityReportDialog(true);
                },
            });
        }

        if (permissions.processingActivityPermissions.delete) {
            menuItems.push({
                menuItem: translateString("deleteProcessingActivity"),
                onClick: () => {
                    setShowDeleteDialog(true);
                },
            });
        }

        if (permissions.canAccessEnterpriseRiskManagement) {
            menuItems.push({
                menuItem: translateString("manageRiskAreas"),
                onClick: () => setShowRiskAreasDialog(true),
            });
        }

        addTaskMenuItem.addCreateTaskMenuItem(menuItems, {
            fromEntityId: id!,
            fromEntityType: EntityType.ProcessingActivity,
            toEntityType: EntityType.Task,
        });

        menuItems.push(entityGraphMenuItem);

        return menuItems;
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={
                    <Box sx={styles.gridContentAlignment}>
                        {canSubmitProcessingActivity && (
                            <DotLegalButton buttonType="primary" onClick={() => setShowProcessingActivityReportDialog(true)}>
                                {translateString("submit")}
                            </DotLegalButton>
                        )}
                        <PageHeaderEditEntityButton menuItems={getEditMenuItems()} />
                    </Box>
                }
            />

            <EntityDetailTopBar>
                <TopBarRow>
                    {addonsFeatures.research && (
                        <TopBarTextTile label={translateString("approvelNumber")} value={topBar?.approvalNumber ?? "-"} isLoading={isTopBarLoading} />
                    )}
                    <TopBarTextTile label={translateString("Purpose")} value={topBar?.purposes ?? "-"} isLoading={isTopBarLoading} />
                    <TopBarTextTile label={translateString("dataSubjects")} value={topBar?.dataSubjects ?? "-"} isLoading={isTopBarLoading} />
                    <TopBarTextTile
                        label={translateString("securityMeasures")}
                        value={getSecurityMeasures(topBar?.securityMeasures)}
                        isLoading={isTopBarLoading}
                    />
                    <TopBarNumberTile
                        label={translateString("NumberOfSubjects")}
                        value={
                            topBar
                                ? getNumberOfDataSubjects(translateDataSubjectsEnum, topBar.numberOfSubjects ?? undefined, topBar.noOfSubjects ?? undefined)
                                : undefined
                        }
                        isLoading={isTopBarLoading}
                    />
                    <TopBarNumberTile label={translateString("NumberOfProcessors")} value={getNumberOfProcessors()} isLoading={isTopBarLoading} />

                    {!addonsFeatures.research && canSeePolicyGrid && policyGrid()}
                </TopBarRow>

                <TopBarRow>
                    {addonsFeatures.research && canSeePolicyGrid && policyGrid()}

                    {ownerGrid}
                    <TopBarChipsTile label={translateString("processingActivityAreas")} chips={topBar?.businessAreas} isLoading={isTopBarLoading} />

                    {hasEnterpriseRiskManagementAccess ? (
                        <RiskAreaRisksTile entityType={EntityType.ProcessingActivity} entityId={id!} />
                    ) : (
                        permissions.canManageRiskAssessment && (
                            <TopBarTile label={translateString("risk")} isLoading={riskAssessmentLoading}>
                                <Box sx={styles.riskAssessmentText}>
                                    <Box sx={styles.textAlignLeft}>
                                        <RiskIndicator
                                            isClassification
                                            riskAssessmentType={RiskAssessmentType.Classification}
                                            fixedWidthLeftColumn
                                            textSize="sm"
                                            score={riskAssessmentData?.scenariosScore}
                                            linkHref={getProcessingActivityRiskScenariosDialog(
                                                id,
                                                riskAssessmentData?.currentRiskAssessmentVersionId ?? undefined
                                            )}
                                        />

                                        <RiskIndicator
                                            riskAssessmentType={RiskAssessmentType.Risk}
                                            fixedWidthLeftColumn
                                            textSize="sm"
                                            score={riskAssessmentData?.riskAssessmentTotalScore}
                                            linkHref={getRiskAssessmentQuestionsDialog(
                                                id,
                                                RiskAssessmentArea.ProcessingActivities,
                                                RiskComplianceArea.GDPR,
                                                riskAssessmentData?.currentRiskAssessmentVersionId ?? "null"
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </TopBarTile>
                        )
                    )}
                    <TopBarTextTile
                        label={translateString("status")}
                        neverShowTooltip
                        value={
                            topBar?.processingActivityStatus !== undefined ? (
                                <ProcessingActivityStatusChip status={topBar.processingActivityStatus} />
                            ) : undefined
                        }
                        isLoading={isTopBarLoading}
                    />

                    {platformFeatures.tags && permissions.canAccessTags && permissions.processingActivityMasterDataPermissions?.permissions.read && (
                        <TopBarChipsTile label={translateString("tags")} chips={topBar?.tags} isLoading={isTopBarLoading} />
                    )}

                    {!addonsFeatures.research && (
                        <Grid size={topBarTileSize} sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                            <Box sx={styles.percentageContainer}>
                                {isTopBarLoading ? (
                                    <Skeleton sx={styles.percentageSkeleton} variant="text" width={"100%"} />
                                ) : (
                                    <>
                                        <DotLegalCompletedPercentage
                                            hoverText={topBar?.isValidationDue ? translateString("processingValidationDue") : ""}
                                            completed={topBar?.progress ?? 0}
                                        />
                                        {getValidation()}
                                    </>
                                )}
                            </Box>
                        </Grid>
                    )}
                </TopBarRow>
            </EntityDetailTopBar>

            <Box sx={styles.tableContainer}>
                <PlanTabs
                    fullWidth={false}
                    tabStyle="secondary"
                    selectedTab={selectedTab ?? 0}
                    setSelectedTab={(index) => setSelectedTab(index)}
                    content={getTabContent()}
                    noPadding
                    ignoreTextTransformation
                />
            </Box>

            {permissions.canManageProcessingActivtyComments && topBar && (
                <CommentDialog
                    processingActivityId={topBar.id}
                    header={topBar.name}
                    disabled={shouldDisableComments(addonsFeatures.research, topBar.processingActivityStatus)}
                    entityType={EntityType.ProcessingActivity}
                />
            )}

            {showDeleteDialog && (
                <ProcessingActivityDeleteDialog
                    id={topBar?.id!}
                    name={topBar?.name!}
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onSuccessDelete={() => {
                        refetchMenuNotifications();
                        onProcessingActivityDeleteSuccess();
                    }}
                />
            )}

            {showRiskAreasDialog && (
                <EntityRiskAreasDialog entityType={EntityType.ProcessingActivity} entityId={id!} onClose={() => setShowRiskAreasDialog(false)} />
            )}

            {showNoteDialog && (
                <DotLegalNoteDialog
                    showDialog={showNoteDialog}
                    noteText={topBar?.note ?? undefined}
                    onDialogClose={() => setShowNoteDialog(false)}
                    onNoteUpdated={() => {
                        refetchTopBar();
                        setShowNoteDialog(false);
                    }}
                    postUrl={`/ProcessingActivity/${id!}/note`}
                    entityId={id}
                    entityType={EntityType.ProcessingActivity}
                />
            )}
            {showPolicyDialog && <PolicyOverviewDialog onDialogClose={() => setShowPolicyDialog(false)} policies={topBar!.policies} />}
            {showStatusDialog && (
                <ProcessingActivityStatusDialog
                    onDialogClose={() => setShowStatusDialog(false)}
                    Status={topBar!.processingActivityStatus}
                    Id={id}
                    onSaveClick={() => refetchTopBar()}
                />
            )}
            {showAuditLogDialog && (
                <AuditLogDialog
                    entityType={EntityType.ProcessingActivity}
                    entityId={id}
                    entityName={topBar?.name}
                    onDialogClose={() => setShowAuditLogDialog(false)}
                />
            )}
            {isEntityGraphOpen && id && <EntityGraphDialog rootType={EntityType.ProcessingActivity} rootId={id} onClose={closeEntityGraph} />}

            <ProcessingActivityReport
                showReportDialog={showProcessingActivityReportDialog}
                processingActivityId={id}
                processingActivityName={topBar?.name}
                onDialogClose={() => setShowProcessingActivityReportDialog(false)}
                processingActivityStatus={topBar?.processingActivityStatus}
                onSubmitReport={refetchTopBar}
            />
        </Box>
    );
}

export default withAITracking(reactPlugin, ProcessingActivityOverview, "Edit processing activity", "tracking");
